import { useCallback, useEffect, useRef } from 'react';
import { useUIStore } from '@/stores/uiStore';
import { MIN_FLEET_PANEL_HEIGHT, MAX_FLEET_PANEL_HEIGHT } from '@/utils/constants';

export function useFleetPanelResize() {
  const fleetPanelHeight = useUIStore((s) => s.fleetPanelHeight);
  const setFleetPanelHeight = useUIStore((s) => s.setFleetPanelHeight);
  const isResizing = useRef(false);
  const resizerRef = useRef<HTMLDivElement>(null);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    document.body.classList.add('resizing-vertical');
    resizerRef.current?.classList.add('active');
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return;
      
      const windowHeight = window.innerHeight;
      const maxHeight = windowHeight * MAX_FLEET_PANEL_HEIGHT;
      
      const newHeight = Math.min(
        Math.max(windowHeight - e.clientY, MIN_FLEET_PANEL_HEIGHT),
        maxHeight
      );
      
      setFleetPanelHeight(newHeight);
    };

    const onMouseUp = () => {
      if (!isResizing.current) return;
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.classList.remove('resizing-vertical');
      resizerRef.current?.classList.remove('active');
      
      // Notify components that may need to resize (like the map)
      window.dispatchEvent(new Event('resize'));
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [setFleetPanelHeight]);

  return { fleetPanelHeight, onMouseDown, resizerRef };
}
